import { getFirebase } from '@/providers/firebase';

export type ApiRequestOptions = {
  headers?: Record<string, string>;
} & RequestInit;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public body?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Makes an HTTP request with automatic auth token injection
 *
 * @param url - The URL to request
 * @param options - Request options including headers and method
 *
 * @returns Promise resolving to the typed response data
 */
export const apiRequest = async <T = any>(
  url: string,
  options: ApiRequestOptions = {},
): Promise<T> => {
  const headers: Record<string, string> = {};

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  // Get auth token from Firebase if user is logged in
  try {
    const firebase = getFirebase();
    const token = await firebase.auth.currentUser?.getIdToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (error) {
    // Firebase not configured or user not logged in - continue without auth
  }

  // Prefix known routes with /api; all other routes fall back to /api/skillsets
  let apiUrl: string;
  if (
    url.startsWith('/auth') ||
    url.startsWith('/oauth') ||
    url.startsWith('/users') ||
    url.startsWith('/admin') ||
    url.startsWith('/skillsets') ||
    url.startsWith('/skills') ||
    url.startsWith('/subagents') ||
    url.startsWith('/search') ||
    url.startsWith('/home') ||
    url.startsWith('/docs') ||
    url.startsWith('/members')
  ) {
    apiUrl = `/api${url}`;
  } else {
    apiUrl = `/api/skillsets${url}`;
  }

  const response = await fetch(apiUrl, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      `API request failed: ${response.status} ${response.statusText}`,
      response.status,
      response.statusText,
      body,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
};
